import type { PlaybackItem, PlaybackStatus } from './player-context.js';
import type { QueueModel, QueuePlaybackContext } from './player-state.js';
import { readStoredJson, removeStoredValue, writeStoredJson } from '../app/durable-state.js';

const VERSION = 1;
const KEY_PREFIX = 'song-composer:playback-session:v1:';

export type PersistedPlaybackSession = {
  item: PlaybackItem;
  model: QueueModel<PlaybackItem>;
  position: number;
  wasPlaying: boolean;
};

type StoredPlaybackSession = PersistedPlaybackSession & { version: typeof VERSION };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPlaybackItem(value: unknown): value is PlaybackItem {
  if (!isRecord(value)) return false;
  const identityIsValid =
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    (value.artist === null || typeof value.artist === 'string') &&
    (value.album === null || typeof value.album === 'string') &&
    typeof value.detailHref === 'string' &&
    (value.composerProjectId === undefined || typeof value.composerProjectId === 'string');
  if (!identityIsValid) return false;
  if (value.sourcePreference === 'preview') return true;
  if (value.sourcePreference === 'direct') return typeof value.directUrl === 'string';
  return (
    value.sourcePreference === 'owned' &&
    typeof value.libraryId === 'string' &&
    typeof value.importId === 'string'
  );
}

function isPlaybackContext(value: unknown): value is QueuePlaybackContext {
  if (!isRecord(value)) return false;
  return (
    ['catalog', 'project', 'playlist', 'library', 'generation', 'delivery', 'single'].includes(
      String(value.kind),
    ) &&
    typeof value.id === 'string' &&
    typeof value.label === 'string'
  );
}

function parseModel(value: unknown): QueueModel<PlaybackItem> | null {
  if (!isRecord(value)) return null;
  if (!Array.isArray(value.canonical) || !value.canonical.every(isPlaybackItem)) return null;
  if (!Array.isArray(value.items) || !value.items.every(isPlaybackItem)) return null;
  if (value.context !== null && !isPlaybackContext(value.context)) return null;
  if (value.index !== null && (!Number.isInteger(value.index) || Number(value.index) < 0))
    return null;
  if (typeof value.shuffle !== 'boolean') return null;
  const index = value.index === null ? null : Number(value.index);
  if (index !== null && index >= value.items.length) return null;
  return {
    canonical: value.canonical,
    context: value.context,
    index,
    items: value.items,
    shuffle: value.shuffle,
  };
}

function storageKey(scope: string) {
  return `${KEY_PREFIX}${encodeURIComponent(scope)}`;
}

export function readPlaybackSession(scope: string): PersistedPlaybackSession | null {
  try {
    const value = readStoredJson('session', storageKey(scope));
    if (!isRecord(value) || value.version !== VERSION || !isPlaybackItem(value.item)) return null;
    const model = parseModel(value.model);
    if (!model) return null;
    if (typeof value.position !== 'number' || !Number.isFinite(value.position)) return null;
    if (typeof value.wasPlaying !== 'boolean') return null;
    return {
      item: value.item,
      model,
      position: Math.max(0, value.position),
      wasPlaying: value.wasPlaying,
    };
  } catch {
    return null;
  }
}

export function writePlaybackSession(scope: string, session: PersistedPlaybackSession): boolean {
  try {
    const stored: StoredPlaybackSession = { version: VERSION, ...session };
    return writeStoredJson('session', storageKey(scope), stored);
  } catch {
    return false;
  }
}

export function clearPlaybackSession(scope: string): void {
  removeStoredValue('session', storageKey(scope));
}

export function shouldPersistAsPlaying(status: PlaybackStatus): boolean {
  return status === 'playing' || status === 'loading' || status === 'waiting';
}
